import React, { useState } from "react";
import PropTypes from "prop-types";
import RightSheet from "../../../components/ui-components/RightSheet";
import UserSearchInput from "../../../components/form-components/UserSearchInput";
import { useSaveUseCaseProfile } from "../hooks/useUseCaseQueries";

// The four stages must match the CHECK constraint on use_case_profile and
// validLifecycleStage in the API. Adding a fifth needs all three changed.
const STAGES = [
  { value: "idea", label: "Idea" },
  { value: "pilot", label: "Pilot" },
  { value: "production", label: "Production" },
  { value: "retired", label: "Retired" },
];

// `theme.width` in tailwind.config.js is overridden outside `extend` with a
// tiny custom scale (see RightSheet.jsx's own note on this), so `w-full`
// silently compiles to nothing. Full width is set inline instead.
const fieldCls =
  "border border-border rounded-ctrl px-3 py-1.5 text-sm bg-card text-text";
const fieldStyle = { width: "100%" };

// seedOwner turns the saved profile into UserSearchInput's value shape. The
// display name comes from the portfolio row (ownerName), which is the only
// place the API resolves the user id to a name; with no name we still show the
// id so the field is never mysteriously blank on a use case that HAS an owner.
function seedOwner(useCase) {
  const id = useCase?.ownerUserId;
  if (id == null) return null;
  const name = useCase?.row?.ownerName;
  return { user_id: String(id), display_name: name || String(id) };
}

export default function UseCaseProfileSheet({ open, onClose, useCase }) {
  const row = useCase?.row ?? {};
  const save = useSaveUseCaseProfile();

  const [stage, setStage] = useState(row.stage || "idea");
  const [startedOn, setStartedOn] = useState(useCase?.startedOn || "");
  const [description, setDescription] = useState(useCase?.description || "");
  const [owner, setOwner] = useState(() => seedOwner(useCase));

  // No `if (!open) return null` here, deliberately. It tore RightSheet out of
  // the tree in the same tick `open` went false, so the shell's own close
  // transition (its `mounted` gate, CLOSE_MS) never ran and the drawer
  // vanished instead of sliding out. RightSheet already handles staying
  // mounted while it animates and rendering nothing once it is done, and the
  // `key={nodeId}-${editSession}` bump on the caller (UseCaseDetailPage)
  // already reseeds the useState above on each open, so the short-circuit
  // bought nothing it did not cost twice over.

  const handleSave = () => {
    save.mutate({
      nodeId: row.nodeId,
      payload: {
        // Number, not the picker's string id: the API binds ownerUserId to an
        // integer column. An empty picker means "no owner", which is a real
        // edit and must round-trip as null rather than be dropped.
        ownerUserId: owner?.user_id ? Number(owner.user_id) : null,
        // orgNodeId is not editable here yet — round-tripped so saving the
        // rest of the profile cannot silently clear it.
        orgNodeId: useCase?.orgNodeId ?? null,
        lifecycleStage: stage,
        startedOn: startedOn || "",
        description,
      },
    });
    onClose();
  };

  return (
    <RightSheet
      open={open}
      onClose={onClose}
      title={`Edit ${row.name || "use case"}`}
      footer={
        <RightSheet.Footer
          primary={{
            label: "Save",
            onClick: handleSave,
            disabled: save.isPending,
            loading: save.isPending,
          }}
        />
      }
    >
      <div className="flex flex-col gap-4">
        <div>
          <span
            id="use-case-profile-owner-label"
            className="block text-xs font-medium text-text-2 mb-2"
          >
            Owner
          </span>
          <UserSearchInput
            value={owner}
            onChange={setOwner}
            placeholder="Search user by name or email…"
          />
        </div>

        <div>
          <label
            htmlFor="use-case-profile-stage"
            className="block text-xs font-medium text-text-2 mb-2"
          >
            Stage
          </label>
          <select
            id="use-case-profile-stage"
            value={stage}
            onChange={(e) => setStage(e.target.value)}
            className={fieldCls}
            style={fieldStyle}
          >
            {STAGES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label
            htmlFor="use-case-profile-started-on"
            className="block text-xs font-medium text-text-2 mb-2"
          >
            Started on
          </label>
          <input
            id="use-case-profile-started-on"
            type="date"
            value={startedOn}
            onChange={(e) => setStartedOn(e.target.value)}
            className={fieldCls}
            style={fieldStyle}
          />
        </div>

        <div>
          <label
            htmlFor="use-case-profile-description"
            className="block text-xs font-medium text-text-2 mb-2"
          >
            Description
          </label>
          <textarea
            id="use-case-profile-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={4}
            className={fieldCls}
            style={fieldStyle}
          />
        </div>
      </div>
    </RightSheet>
  );
}

UseCaseProfileSheet.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  useCase: PropTypes.shape({
    row: PropTypes.shape({
      nodeId: PropTypes.number,
      name: PropTypes.string,
      stage: PropTypes.string,
      ownerName: PropTypes.string,
    }),
    description: PropTypes.string,
    startedOn: PropTypes.string,
    ownerUserId: PropTypes.number,
    orgNodeId: PropTypes.number,
  }),
};
